'use client'

import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { FeatureShell, ChatPanel, SoftCard, Prose } from './ui'
import * as AI from '@/lib/ai'

// ============================================================================
// FEATURE METADATA + REGISTRY
// ============================================================================
export type Tone = 'day' | 'night'

export interface FeatureMeta {
  id: string
  emoji: string
  title: string
  subtitle: string
  blurb: string
  accent: string
  tone: Tone
  group: 'core' | 'learn' | 'calm' | 'grow'
}

export const FEATURES: FeatureMeta[] = [
  { id: 'ted', emoji: '🧸', title: 'Professor Ted', subtitle: 'Your wise, warm mentor for life, school, and every decision', blurb: 'Thoughtful guidance on anything on your mind', accent: '#a9866a', tone: 'day', group: 'core' },
  { id: 'sleep', emoji: '🌙', title: 'Sleep', subtitle: 'Wind down with Willow the Fawn, your gentle night companion', blurb: 'A peaceful nighttime ritual', accent: '#6b74a0', tone: 'night', group: 'calm' },
  { id: 'reflection', emoji: '🌷', title: 'Reflection Space', subtitle: 'A safe, private place for your thoughts, prayers, and lessons', blurb: 'Write freely — receive gentle words & scripture', accent: '#c48a97', tone: 'day', group: 'grow' },
  { id: 'lily', emoji: '🔎', title: 'Research Helper', subtitle: 'Powered by Lily the Bee — clear, real, useful explanations', blurb: 'Understand anything, clearly explained', accent: '#c9a227', tone: 'day', group: 'learn' },
  { id: 'study', emoji: '📖', title: 'Study Space', subtitle: 'Focused help that makes hard things feel possible', blurb: 'Break down and master your studies', accent: '#8a6b52', tone: 'day', group: 'learn' },
  { id: 'focus', emoji: '⏳', title: 'Focus Timer', subtitle: 'Gentle focus sessions to help you find your flow', blurb: 'A calm pomodoro rhythm', accent: '#9bb188', tone: 'day', group: 'learn' },
  { id: 'briefing', emoji: '☀️', title: 'Daily Briefing', subtitle: 'A soft, encouraging start to your day', blurb: 'Your calm morning overview', accent: '#d9a441', tone: 'day', group: 'core' },
  { id: 'decision', emoji: '🎯', title: 'Decision Desk', subtitle: 'Think through any choice with calm, honest clarity', blurb: 'Weigh options and find your answer', accent: '#b5765a', tone: 'day', group: 'grow' },
  { id: 'school', emoji: '🎒', title: 'School Toolkit', subtitle: 'Everything you need to feel on top of school', blurb: 'Plans, tips, and study strategies', accent: '#8a6b52', tone: 'day', group: 'learn' },
  { id: 'calm', emoji: '☁️', title: 'Calm Corner', subtitle: 'A soft place to slow down and breathe', blurb: 'Soothing words when the world is loud', accent: '#9aa7c7', tone: 'day', group: 'calm' },
  { id: 'lounge', emoji: '🕯️', title: 'Learning Lounge', subtitle: 'Cozy, curious learning about anything you love', blurb: 'Explore ideas at your own pace', accent: '#b58a5a', tone: 'day', group: 'learn' },
  { id: 'growth', emoji: '🌱', title: 'Personal Growth', subtitle: 'Small, gentle steps toward the person you\'re becoming', blurb: 'Reflect, grow, and be encouraged', accent: '#9bb188', tone: 'day', group: 'grow' },
  { id: 'wisdom', emoji: '📜', title: 'Wisdom Library', subtitle: 'Timeless quotes and gentle truths to carry with you', blurb: 'A collection of little lights', accent: '#a9866a', tone: 'day', group: 'grow' },
  { id: 'questions', emoji: '💡', title: 'Question Corner', subtitle: 'Ask anything — get an honest, thoughtful answer', blurb: 'No question is too small', accent: '#d9a441', tone: 'day', group: 'core' },
  { id: 'organizer', emoji: '🌸', title: 'Life Organizer', subtitle: 'Gently untangle your tasks and your day', blurb: 'Sort the mind, soften the load', accent: '#c48a97', tone: 'day', group: 'grow' },
  { id: 'quiet', emoji: '🦌', title: 'Quiet Space', subtitle: 'Just breathe. Nothing is required of you here', blurb: 'Stillness and a slow breath', accent: '#9bb188', tone: 'day', group: 'calm' },
]

// ============================================================================
// GENERIC CHAT FEATURE
// ============================================================================
function ChatFeature({
  meta,
  onBack,
  respond,
  greeting,
  placeholder,
  aiAvatar,
  quickPrompts,
}: {
  meta: FeatureMeta
  onBack: () => void
  respond: (input: string) => { text: string; verse?: { ref: string; text: string } }
  greeting: string
  placeholder: string
  aiAvatar: string
  quickPrompts?: string[]
}) {
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} tone={meta.tone} accent={meta.accent}>
      <ChatPanel
        respond={respond}
        greeting={greeting}
        placeholder={placeholder}
        tone={meta.tone}
        accent={meta.accent}
        aiAvatar={aiAvatar}
        storageKey={`hf_chat_${meta.id}`}
        quickPrompts={quickPrompts || []}
      />
    </FeatureShell>
  )
}

const wrap = (s: string) => ({ text: s })

// ============================================================================
// REFLECTION SPACE 🌷 — chat + 5 sub-features + devotion
// ============================================================================
function ReflectionSpace({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const tabs = [
    { id: 'reflect', label: 'Reflect 🌷' },
    { id: 'devotion', label: 'Devotion 🌿' },
    { id: 'study', label: 'Bible Study 📖' },
    { id: 'prayer', label: 'Prayer Space 🙏' },
    { id: 'gratitude', label: 'Gratitude 🌻' },
    { id: 'scripture', label: 'Scripture Explorer 📜' },
    { id: 'journal', label: 'Journaling ✍️' },
  ]
  const [tab, setTab] = useState('reflect')

  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} tone="day" accent={meta.accent}>
      <div className="mb-4 flex flex-wrap justify-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-2 text-base transition-all ${
              tab === t.id ? 'text-white shadow-cozy' : 'bg-white/70 text-fawn-600 shadow-soft hover:bg-white'
            }`}
            style={tab === t.id ? { backgroundColor: meta.accent } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'reflect' && (
        <ChatPanel
          respond={(input) => {
            const { message, verse } = AI.reflectionReply(input)
            return { text: message, verse }
          }}
          greeting="This is your safe, private space. 🌷 Write whatever is on your heart — a worry, a frustration, a lesson, a hope. Nothing here is judged. After you share, I'll offer gentle words and a scripture just for you."
          placeholder="Pour out your thoughts here…"
          accent={meta.accent}
          aiAvatar="🌷"
          storageKey="hf_reflection"
          quickPrompts={["I'm feeling overwhelmed", "I need encouragement", "I'm thankful today"]}
        />
      )}

      {tab === 'devotion' && <DevotionTab accent={meta.accent} />}
      {tab === 'study' && <BibleStudyTab accent={meta.accent} />}
      {tab === 'prayer' && <PrayerTab accent={meta.accent} />}
      {tab === 'gratitude' && <GratitudeTab accent={meta.accent} />}
      {tab === 'scripture' && <ScriptureTab accent={meta.accent} />}
      {tab === 'journal' && <JournalTab accent={meta.accent} />}
    </FeatureShell>
  )
}

function DevotionTab({ accent }: { accent: string }) {
  const [dev, setDev] = useState<AI.Devotion | null>(null)
  useEffect(() => setDev(AI.randomDevotion()), [])
  if (!dev) return null
  return (
    <div className="mx-auto max-w-3xl">
      <SoftCard>
        <p className="text-center text-sm uppercase tracking-[0.3em] text-fawn-400">Today&apos;s Devotion</p>
        <h2 className="mt-2 text-center font-display text-3xl font-bold" style={{ color: accent }}>{dev.title}</h2>
        <div className="my-5 rounded-2xl border-l-4 border-blush-300 bg-blush-200/40 px-5 py-4">
          <p className="text-xl italic leading-relaxed text-fawn-700">&ldquo;{dev.verse.text}&rdquo;</p>
          <p className="mt-2 text-right font-display font-semibold" style={{ color: accent }}>— {dev.verse.ref}</p>
        </div>
        <p className="text-xl leading-relaxed text-fawn-700">{dev.body}</p>
        <div className="mt-5 rounded-2xl bg-cream-200/60 px-5 py-4">
          <p className="mb-1 font-display font-semibold" style={{ color: accent }}>A Prayer 🙏</p>
          <p className="text-xl italic leading-relaxed text-fawn-600">{dev.prayer}</p>
        </div>
      </SoftCard>
      <div className="mt-4 text-center">
        <button
          onClick={() => setDev(AI.randomDevotion())}
          className="rounded-full px-6 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5"
          style={{ backgroundColor: accent }}
        >
          Give Me a Devotion 🌿
        </button>
      </div>
    </div>
  )
}

function BibleStudyTab({ accent }: { accent: string }) {
  const [study, setStudy] = useState<AI.BibleStudy | null>(null)
  useEffect(() => setStudy(AI.randomBibleStudy()), [])
  if (!study) return null
  return (
    <div className="mx-auto max-w-3xl">
      <SoftCard>
        <p className="text-center text-sm uppercase tracking-[0.3em] text-fawn-400">Guided Bible Study</p>
        <h2 className="mt-2 text-center font-display text-3xl font-bold" style={{ color: accent }}>{study.theme}</h2>
        <div className="my-5 rounded-2xl border-l-4 border-blush-300 bg-blush-200/40 px-5 py-4">
          <p className="text-xl italic leading-relaxed text-fawn-700">&ldquo;{study.passage.text}&rdquo;</p>
          <p className="mt-2 text-right font-display font-semibold" style={{ color: accent }}>— {study.passage.ref}</p>
        </div>
        <h3 className="font-display text-xl font-semibold" style={{ color: accent }}>Observe</h3>
        <p className="mb-4 text-xl leading-relaxed text-fawn-700">{study.observe}</p>
        <h3 className="font-display text-xl font-semibold" style={{ color: accent }}>Reflect</h3>
        <ul className="mb-4 space-y-2">
          {study.reflect.map((r, i) => (
            <li key={i} className="flex gap-2 text-xl leading-relaxed text-fawn-700"><span>🌷</span><span>{r}</span></li>
          ))}
        </ul>
        <h3 className="font-display text-xl font-semibold" style={{ color: accent }}>Apply</h3>
        <p className="text-xl leading-relaxed text-fawn-700">{study.apply}</p>
      </SoftCard>
      <div className="mt-4 text-center">
        <button onClick={() => setStudy(AI.randomBibleStudy())} className="rounded-full px-6 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5" style={{ backgroundColor: accent }}>
          Another Study 📖
        </button>
      </div>
    </div>
  )
}

function PrayerTab({ accent }: { accent: string }) {
  const [prayer, setPrayer] = useState(AI.randomPrayer())
  const [own, setOwn] = useState('')
  const [saved, setSaved] = useState<string[]>([])
  useEffect(() => {
    try { const r = localStorage.getItem('hf_prayers'); if (r) setSaved(JSON.parse(r)) } catch {}
  }, [])
  const savePrayer = () => {
    if (!own.trim()) return
    const next = [own.trim(), ...saved].slice(0, 30)
    setSaved(next); setOwn('')
    try { localStorage.setItem('hf_prayers', JSON.stringify(next)) } catch {}
  }
  return (
    <div className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2">
      <SoftCard>
        <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">A Prayer for You</p>
        <h3 className="mt-2 font-display text-2xl font-bold" style={{ color: accent }}>{prayer.title}</h3>
        <p className="mt-3 text-xl italic leading-relaxed text-fawn-700">{prayer.text}</p>
        <button onClick={() => setPrayer(AI.randomPrayer())} className="mt-5 rounded-full px-5 py-2.5 text-base font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5" style={{ backgroundColor: accent }}>
          Another Prayer 🙏
        </button>
      </SoftCard>
      <SoftCard>
        <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">Your Prayer Wall</p>
        <textarea value={own} onChange={(e) => setOwn(e.target.value)} rows={3} placeholder="Write your own prayer or request…" className="mt-3 w-full resize-none rounded-2xl bg-white/90 px-4 py-3 text-lg text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
        <button onClick={savePrayer} className="mt-2 rounded-full px-5 py-2.5 text-base font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5" style={{ backgroundColor: accent }}>
          Keep This Prayer
        </button>
        <div className="mt-4 space-y-2">
          {saved.map((p, i) => (
            <div key={i} className="rounded-2xl bg-cream-200/60 px-4 py-2 text-lg text-fawn-600">🌷 {p}</div>
          ))}
        </div>
      </SoftCard>
    </div>
  )
}

function GratitudeTab({ accent }: { accent: string }) {
  const [prompt, setPrompt] = useState('')
  const [entry, setEntry] = useState('')
  const [list, setList] = useState<{ text: string; when: string }[]>([])
  useEffect(() => {
    setPrompt(AI.gratitudePrompt())
    try { const r = localStorage.getItem('hf_gratitude'); if (r) setList(JSON.parse(r)) } catch {}
  }, [])
  const add = () => {
    if (!entry.trim()) return
    const next = [{ text: entry.trim(), when: new Date().toLocaleDateString() }, ...list].slice(0, 60)
    setList(next); setEntry(''); setPrompt(AI.gratitudePrompt())
    try { localStorage.setItem('hf_gratitude', JSON.stringify(next)) } catch {}
  }
  return (
    <div className="mx-auto max-w-3xl">
      <SoftCard>
        <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">Gratitude</p>
        <p className="mt-2 text-2xl font-display" style={{ color: accent }}>🌻 {prompt}</p>
        <textarea value={entry} onChange={(e) => setEntry(e.target.value)} rows={2} placeholder="I'm grateful for…" className="mt-3 w-full resize-none rounded-2xl bg-white/90 px-4 py-3 text-lg text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
        <div className="mt-2 flex gap-2">
          <button onClick={add} className="rounded-full px-5 py-2.5 text-base font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5" style={{ backgroundColor: accent }}>Add to Garden</button>
          <button onClick={() => setPrompt(AI.gratitudePrompt())} className="rounded-full bg-white/70 px-5 py-2.5 text-base text-fawn-600 shadow-soft hover:bg-white">New Prompt</button>
        </div>
      </SoftCard>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {list.map((g, i) => (
          <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl bg-white/75 px-4 py-3 shadow-soft">
            <p className="text-lg text-fawn-700">🌷 {g.text}</p>
            <p className="mt-1 text-sm text-fawn-400">{g.when}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function ScriptureTab({ accent }: { accent: string }) {
  const themes = Object.keys(AI.SCRIPTURE_LIBRARY)
  const [theme, setTheme] = useState(themes[0])
  const verses = AI.SCRIPTURE_LIBRARY[theme]
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex flex-wrap justify-center gap-2">
        {themes.map((t) => (
          <button key={t} onClick={() => setTheme(t)} className={`rounded-full px-4 py-1.5 text-base capitalize transition-all ${theme === t ? 'text-white shadow-cozy' : 'bg-white/70 text-fawn-600 shadow-soft hover:bg-white'}`} style={theme === t ? { backgroundColor: accent } : {}}>
            {t}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {verses.map((v, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <SoftCard>
              <p className="text-xl italic leading-relaxed text-fawn-700">&ldquo;{v.text}&rdquo;</p>
              <p className="mt-2 text-right font-display font-semibold" style={{ color: accent }}>— {v.ref}</p>
            </SoftCard>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function JournalTab({ accent }: { accent: string }) {
  const [prompt, setPrompt] = useState('')
  const [text, setText] = useState('')
  const [entries, setEntries] = useState<{ text: string; when: string }[]>([])
  useEffect(() => {
    setPrompt(AI.journalPrompt())
    try { const r = localStorage.getItem('hf_journal'); if (r) setEntries(JSON.parse(r)) } catch {}
  }, [])
  const save = () => {
    if (!text.trim()) return
    const next = [{ text: text.trim(), when: new Date().toLocaleString() }, ...entries].slice(0, 60)
    setEntries(next); setText(''); setPrompt(AI.journalPrompt())
    try { localStorage.setItem('hf_journal', JSON.stringify(next)) } catch {}
  }
  return (
    <div className="mx-auto max-w-3xl">
      <SoftCard>
        <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">Peaceful Journaling</p>
        <p className="mt-2 text-2xl font-display" style={{ color: accent }}>✍️ {prompt}</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Let your thoughts flow gently onto the page…" className="mt-3 w-full resize-none rounded-2xl bg-white/90 px-4 py-3 text-lg leading-relaxed text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
        <div className="mt-2 flex gap-2">
          <button onClick={save} className="rounded-full px-5 py-2.5 text-base font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5" style={{ backgroundColor: accent }}>Save Entry</button>
          <button onClick={() => setPrompt(AI.journalPrompt())} className="rounded-full bg-white/70 px-5 py-2.5 text-base text-fawn-600 shadow-soft hover:bg-white">New Prompt</button>
        </div>
      </SoftCard>
      <div className="mt-4 space-y-3">
        {entries.map((e, i) => (
          <div key={i} className="rounded-2xl bg-white/75 px-5 py-4 shadow-soft">
            <p className="whitespace-pre-wrap text-lg leading-relaxed text-fawn-700">{e.text}</p>
            <p className="mt-2 text-sm text-fawn-400">{e.when}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// FOCUS TIMER ⏳
// ============================================================================
function FocusTimer({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const presets = [
    { label: 'Focus', mins: 25 },
    { label: 'Short Break', mins: 5 },
    { label: 'Deep Work', mins: 50 },
    { label: 'Long Break', mins: 15 },
  ]
  const [mins, setMins] = useState(25)
  const [secondsLeft, setSecondsLeft] = useState(25 * 60)
  const [running, setRunning] = useState(false)
  const [label, setLabel] = useState('Focus')
  const [completed, setCompleted] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            setRunning(false)
            setCompleted((c) => c + 1)
            return 0
          }
          return s - 1
        })
      }, 1000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [running])

  const choose = (m: number, l: string) => {
    setRunning(false); setMins(m); setLabel(l); setSecondsLeft(m * 60)
  }
  const total = mins * 60
  const pct = ((total - secondsLeft) / total) * 100
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
  const ss = String(secondsLeft % 60).padStart(2, '0')

  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="mx-auto max-w-xl text-center">
        <div className="mb-5 flex flex-wrap justify-center gap-2">
          {presets.map((p) => (
            <button key={p.label} onClick={() => choose(p.mins, p.label)} className={`rounded-full px-4 py-2 text-base transition-all ${label === p.label ? 'text-white shadow-cozy' : 'bg-white/70 text-fawn-600 shadow-soft hover:bg-white'}`} style={label === p.label ? { backgroundColor: meta.accent } : {}}>
              {p.label} · {p.mins}m
            </button>
          ))}
        </div>
        <div className="relative mx-auto flex h-72 w-72 items-center justify-center">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#f3e7d6" strokeWidth="6" />
            <circle cx="50" cy="50" r="45" fill="none" stroke={meta.accent} strokeWidth="6" strokeLinecap="round" strokeDasharray={2 * Math.PI * 45} strokeDashoffset={2 * Math.PI * 45 * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset 1s linear' }} />
          </svg>
          <div>
            <div className="font-display text-6xl font-bold text-fawn-700">{mm}:{ss}</div>
            <div className="mt-1 text-lg text-fawn-400">{label} 🦌</div>
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <button onClick={() => setRunning((r) => !r)} className="rounded-full px-8 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5" style={{ backgroundColor: meta.accent }}>
            {running ? 'Pause' : secondsLeft === 0 ? 'Done ✨' : 'Begin'}
          </button>
          <button onClick={() => choose(mins, label)} className="rounded-full bg-white/70 px-8 py-3 text-lg text-fawn-600 shadow-soft hover:bg-white">Reset</button>
        </div>
        <p className="mt-6 text-lg text-fawn-500">Sessions completed today: <span className="font-semibold" style={{ color: meta.accent }}>{completed}</span> 🌸</p>
        <p className="mt-2 text-lg text-fawn-400">Work gently. When your mind wanders, guide it back like a fawn to the path.</p>
      </div>
    </FeatureShell>
  )
}

// ============================================================================
// DAILY BRIEFING ☀️
// ============================================================================
function DailyBriefing({ meta, onBack, name, balance }: { meta: FeatureMeta; onBack: () => void; name: string; balance: number }) {
  const [lines, setLines] = useState<string[]>([])
  useEffect(() => setLines(AI.dailyBriefing(name, balance)), [name, balance])
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="mx-auto max-w-2xl space-y-4">
        {lines.map((l, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.18 }}>
            <SoftCard>
              <Prose text={l} className="text-xl text-fawn-700" />
            </SoftCard>
          </motion.div>
        ))}
        <div className="text-center">
          <button onClick={() => setLines(AI.dailyBriefing(name, balance))} className="rounded-full px-6 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5" style={{ backgroundColor: meta.accent }}>
            Refresh Briefing ☀️
          </button>
        </div>
      </div>
    </FeatureShell>
  )
}

// ============================================================================
// DECISION DESK 🎯
// ============================================================================
function DecisionDesk({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const [ctx, setCtx] = useState('')
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [result, setResult] = useState('')
  const analyze = () => setResult(AI.decisionAnalysis(ctx, a, b))
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="mx-auto max-w-3xl">
        <SoftCard>
          <label className="text-lg font-semibold text-fawn-600">What are you deciding?</label>
          <input value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="e.g. Should I change my major?" className="mt-2 w-full rounded-2xl bg-white/90 px-4 py-3 text-lg text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input value={a} onChange={(e) => setA(e.target.value)} placeholder="Option A" className="rounded-2xl bg-white/90 px-4 py-3 text-lg text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
            <input value={b} onChange={(e) => setB(e.target.value)} placeholder="Option B" className="rounded-2xl bg-white/90 px-4 py-3 text-lg text-fawn-700 shadow-soft outline-none placeholder:text-fawn-400" />
          </div>
          <button onClick={analyze} className="mt-4 rounded-full px-6 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5" style={{ backgroundColor: meta.accent }}>
            Help Me Decide 🎯
          </button>
        </SoftCard>
        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            <SoftCard>
              <Prose text={result} className="text-xl text-fawn-700" />
            </SoftCard>
          </motion.div>
        )}
      </div>
    </FeatureShell>
  )
}

// ============================================================================
// WISDOM LIBRARY 📜
// ============================================================================
function WisdomLibrary({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const [quote, setQuote] = useState(AI.WISDOM_QUOTES[0])
  useEffect(() => setQuote(AI.randomWisdom()), [])
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="mx-auto max-w-2xl">
        <motion.div key={quote.text} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <SoftCard className="text-center">
            <div className="text-5xl">📜</div>
            <p className="mt-4 font-display text-3xl font-medium leading-snug text-fawn-700">&ldquo;{quote.text}&rdquo;</p>
            <p className="mt-4 text-lg uppercase tracking-[0.3em] text-fawn-400">{quote.by}</p>
          </SoftCard>
        </motion.div>
        <div className="mt-5 text-center">
          <button onClick={() => setQuote(AI.randomWisdom())} className="rounded-full px-6 py-3 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5" style={{ backgroundColor: meta.accent }}>
            Another Light ✨
          </button>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {AI.WISDOM_QUOTES.map((q, i) => (
            <button key={i} onClick={() => setQuote(q)} className="rounded-2xl bg-white/70 px-4 py-3 text-left text-lg text-fawn-600 shadow-soft transition-all hover:-translate-y-0.5 hover:bg-white">
              &ldquo;{q.text}&rdquo;
            </button>
          ))}
        </div>
      </div>
    </FeatureShell>
  )
}

// ============================================================================
// PERSONAL GROWTH 🌱 — chat + daily challenge
// ============================================================================
function PersonalGrowth({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const [challenge, setChallenge] = useState('')
  useEffect(() => setChallenge(AI.growthChallenge()), [])
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="mx-auto mb-4 max-w-3xl">
        <SoftCard className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">Today&apos;s Gentle Challenge</p>
            <p className="mt-1 text-2xl font-display text-fawn-700">🌱 {challenge}</p>
          </div>
          <button onClick={() => setChallenge(AI.growthChallenge())} className="whitespace-nowrap rounded-full px-5 py-2.5 text-base font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5" style={{ backgroundColor: meta.accent }}>
            New Challenge
          </button>
        </SoftCard>
      </div>
      <ChatPanel
        respond={(input) => wrap(AI.professorTed(input))}
        greeting="Welcome to your growth space. 🌱 Tell me where you'd like to grow — a habit, a mindset, a fear you're ready to face — and we'll take gentle, honest steps together."
        placeholder="Where do you want to grow?"
        accent={meta.accent}
        aiAvatar="🌱"
        storageKey="hf_growth"
        quickPrompts={["I keep procrastinating", "I want more confidence", "Help me build a habit"]}
      />
    </FeatureShell>
  )
}

// ============================================================================
// QUIET SPACE 🦌 — breathing
// ============================================================================
function QuietSpace({ meta, onBack }: { meta: FeatureMeta; onBack: () => void }) {
  const [phase, setPhase] = useState<'in' | 'hold' | 'out'>('in')
  const phases = { in: { label: 'Breathe in…', dur: 4000, scale: 1.4 }, hold: { label: 'Hold…', dur: 4000, scale: 1.4 }, out: { label: 'Breathe out…', dur: 6000, scale: 1 } }
  useEffect(() => {
    const order: ('in' | 'hold' | 'out')[] = ['in', 'hold', 'out']
    let i = 0
    const tick = () => {
      const cur = order[i % 3]
      setPhase(cur)
      i++
    }
    tick()
    const id = setInterval(tick, 4666)
    return () => clearInterval(id)
  }, [])
  return (
    <FeatureShell emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} onBack={onBack} accent={meta.accent}>
      <div className="flex h-[calc(100vh-300px)] min-h-[380px] flex-col items-center justify-center">
        <motion.div
          animate={{ scale: phases[phase].scale }}
          transition={{ duration: phase === 'out' ? 6 : 4, ease: 'easeInOut' }}
          className="flex h-56 w-56 items-center justify-center rounded-full shadow-cozy"
          style={{ backgroundColor: meta.accent + '33', border: `2px solid ${meta.accent}55` }}
        >
          <div className="text-6xl">🦌</div>
        </motion.div>
        <motion.p key={phase} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-10 font-display text-3xl text-fawn-600">
          {phases[phase].label}
        </motion.p>
        <p className="mt-4 max-w-md text-center text-xl text-fawn-400">
          There is nothing to do here but breathe. Let the meadow hold you for a while. ☁️
        </p>
      </div>
    </FeatureShell>
  )
}

// ============================================================================
// FEATURE RENDERER
// ============================================================================
export function FeatureRenderer({
  id,
  onBack,
  name,
  balance,
}: {
  id: string
  onBack: () => void
  name: string
  balance: number
}) {
  const meta = FEATURES.find((f) => f.id === id)
  if (!meta) return null

  switch (id) {
    case 'ted':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.professorTed(i))} aiAvatar="🧸"
        greeting="Hello, dear one. 🧸 I'm Professor Ted. You can talk to me about anything — school, life, a problem you're turning over, a decision, or just a feeling. Tell me what's on your mind, and I'll give it my full, thoughtful attention."
        placeholder="Tell Professor Ted anything…" quickPrompts={["I'm stressed about school", "Help me make a decision", "I feel unmotivated"]} />
    case 'sleep':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.willowTheFawn(i))} aiAvatar="🦌"
        greeting="🌙 Hi, I'm Willow, a little fawn who keeps watch over sleepy hearts. The day is done now. Tell me how you're feeling as the night settles in, and I'll help you drift somewhere soft and peaceful."
        placeholder="How are you feeling tonight?" quickPrompts={["My mind won't stop", "Help me wind down", "Tell me a sleepy story"]} />
    case 'reflection':
      return <ReflectionSpace meta={meta} onBack={onBack} />
    case 'lily':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.lilyTheBee(i))} aiAvatar="🐝"
        greeting="🐝 Hello! I'm Lily the Bee, your research helper. Ask me to explain anything — a concept, a word, how something works, why something happens — and I'll give you a real, clear, genuinely useful answer built around exactly what you want to understand."
        placeholder="What would you like to understand?" quickPrompts={["Explain how something works", "What does this mean?", "Help me research a topic"]} />
    case 'study':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.professorTed(i))} aiAvatar="📖"
        greeting="📖 Welcome to your Study Space. Tell me what you're studying or struggling with, and we'll break it into small, doable pieces and make it feel possible."
        placeholder="What are you studying?" quickPrompts={["I have an exam soon", "I don't understand this topic", "Help me make a study plan"]} />
    case 'focus':
      return <FocusTimer meta={meta} onBack={onBack} />
    case 'briefing':
      return <DailyBriefing meta={meta} onBack={onBack} name={name} balance={balance} />
    case 'decision':
      return <DecisionDesk meta={meta} onBack={onBack} />
    case 'school':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.professorTed(i))} aiAvatar="🎒"
        greeting="🎒 Your School Toolkit is ready. Ask me about assignments, exams, planning, or a subject that's tripping you up — I'll help you feel calm and on top of it."
        placeholder="What do you need help with at school?" quickPrompts={["Plan my week", "Help me study smarter", "I'm behind on assignments"]} />
    case 'calm':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.professorTed(i))} aiAvatar="☁️"
        greeting="☁️ Welcome to the Calm Corner. Take a slow breath. Tell me what's making things feel heavy or loud, and I'll help you gently set it down."
        placeholder="What's on your heart right now?" quickPrompts={["I feel overwhelmed", "I'm anxious", "I need to slow down"]} />
    case 'lounge':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.lilyTheBee(i))} aiAvatar="🕯️"
        greeting="🕯️ Welcome to the Learning Lounge — a cozy place to be curious. What would you love to explore or understand today? I'll make it clear and interesting."
        placeholder="What are you curious about?" quickPrompts={["Teach me something new", "Explain a big idea simply", "Why does this happen?"]} />
    case 'growth':
      return <PersonalGrowth meta={meta} onBack={onBack} />
    case 'wisdom':
      return <WisdomLibrary meta={meta} onBack={onBack} />
    case 'questions':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.questionAnswer(i))} aiAvatar="💡"
        greeting="💡 This is the Question Corner. No question is too small or too big. Ask me anything at all, and I'll give you an honest, thoughtful answer."
        placeholder="Ask me anything…" quickPrompts={["Why do I feel this way?", "How should I handle this?", "What should I do?"]} />
    case 'organizer':
      return <ChatFeature meta={meta} onBack={onBack} respond={(i) => wrap(AI.professorTed(i))} aiAvatar="🌸"
        greeting="🌸 Welcome to your Life Organizer. Tell me everything swirling in your head — tasks, worries, plans — and we'll gently sort it into something clear and doable."
        placeholder="What's on your plate?" quickPrompts={["I have too much to do", "Help me prioritize", "Organize my week"]} />
    case 'quiet':
      return <QuietSpace meta={meta} onBack={onBack} />
    default:
      return null
  }
}
